import { useCallback, useEffect, useMemo, useRef } from 'react';
import { PhoneHoursScheduleRule } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { isEqual } from 'lodash-es';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';
import { PhoneHoursNodeState } from '../call-routes/call-route-details/add-step-panel/types';
import { PhoneHoursForm } from './phone-hours-form/phone-hours-form';
import { usePhoneHoursForm } from './phone-hours-form/use-phone-hours-form';
import { dedupeByScheduleId, normalizePhoneHours } from './phone-hours-step-form.helpers';

export interface PhoneHoursStepFormProps {
  initialState?: {
    callObject?: {
      primitiveId?: string;
      primitiveName?: string;
      instructionId?: string;
      instructionSetId?: string;
      phoneHoursExpansion?: {
        phoneHours?: PhoneHoursScheduleRule[];
      };
    };
  };
  onSave: (nodeUpdate: PhoneHoursNodeState) => void;
  onCancel?: () => void;
  onFormChanges?: (hasChanges: boolean) => void;
  onCanSaveChange?: (canSave: boolean) => void;
  onPhoneHoursChange?: (phoneHours: PhoneHoursScheduleRule[]) => void;
  saveRequestId?: number;
  compact?: boolean;
  isSmallMode?: boolean;
}

export const PhoneHoursStepForm = ({
  initialState,
  onSave,
  onCancel,
  onFormChanges,
  onCanSaveChange,
  onPhoneHoursChange,
  saveRequestId,
  compact = false,
  isSmallMode = false,
}: PhoneHoursStepFormProps) => {
  const { t } = useTranslation('phone');

  const { formProps, getPhoneHours, hasAnyBreakConflict } = usePhoneHoursForm({
    initialName: initialState?.callObject?.primitiveName,
    phoneHours: initialState?.callObject?.phoneHoursExpansion?.phoneHours ?? [],
    keepNewIds: true,
  });

  const nameValue = formProps.nameForm.getFieldProps('name').value;
  const emptyNameField = nameValue === '';
  const hasEmptyBreakName = useMemo(
    () => Object.values(formProps.breakSchedules).some((breakSchedule) => breakSchedule.name.trim() === ''),
    [formProps.breakSchedules]
  );
  const lastPreviewPhoneHoursRef = useRef<PhoneHoursScheduleRule[] | null>(null);
  const lastHandledSaveRequestRef = useRef<number | null>(null);
  const currentPhoneHours = useMemo(() => getPhoneHours(), [getPhoneHours]);

  const hasFormChanges = useMemo(() => {
    const initialPhoneHours = initialState?.callObject?.phoneHoursExpansion?.phoneHours ?? [];

    const nameChanged = nameValue !== initialState?.callObject?.primitiveName;

    const currentScheduleIds = new Set(currentPhoneHours.map((item) => item.scheduleId));

    // 1. Remove items not in current
    const filteredInitial = initialPhoneHours.filter((item) => currentScheduleIds.has(item.scheduleId));

    // 2. Remove duplicates
    const dedupedInitial = dedupeByScheduleId(filteredInitial);

    const phoneHoursChanged = !isEqual(normalizePhoneHours(currentPhoneHours), normalizePhoneHours(dedupedInitial));

    return nameChanged || phoneHoursChanged;
  }, [
    currentPhoneHours,
    initialState?.callObject?.phoneHoursExpansion?.phoneHours,
    initialState?.callObject?.primitiveName,
    nameValue,
  ]);

  // Notify parent when form changes state
  useEffect(() => {
    onFormChanges?.(hasFormChanges);
  }, [onFormChanges, hasFormChanges]);

  const canSave = hasFormChanges && !emptyNameField && !hasEmptyBreakName && !hasAnyBreakConflict;

  useEffect(() => {
    onCanSaveChange?.(canSave);
  }, [onCanSaveChange, canSave]);

  useEffect(() => {
    if (!onPhoneHoursChange) return;

    if (!isEqual(lastPreviewPhoneHoursRef.current, currentPhoneHours)) {
      lastPreviewPhoneHoursRef.current = currentPhoneHours;
      onPhoneHoursChange(currentPhoneHours);
    }
  }, [onPhoneHoursChange, currentPhoneHours]);

  const handleSaveClick = useCallback(() => {
    const nodeUpdate: PhoneHoursNodeState = {
      type: CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS,
      callObject: {
        primitiveId: initialState?.callObject?.primitiveId ?? '',
        primitiveName: nameValue,
        instructionId: initialState?.callObject?.instructionId ?? '',
        instructionSetId: initialState?.callObject?.instructionSetId ?? '',
        phoneHoursExpansion: {
          phoneHours: currentPhoneHours,
        },
      },
    };
    onSave(nodeUpdate);
  }, [
    initialState?.callObject?.primitiveId,
    initialState?.callObject?.instructionId,
    initialState?.callObject?.instructionSetId,
    nameValue,
    currentPhoneHours,
    onSave,
  ]);

  // Parent can request a save without reaching into the DOM.
  useEffect(() => {
    if (saveRequestId === undefined) return;
    if (lastHandledSaveRequestRef.current === saveRequestId) return;

    lastHandledSaveRequestRef.current = saveRequestId;
    if (!canSave) return;

    handleSaveClick();
  }, [saveRequestId, canSave, handleSaveClick]);

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? theme.spacing(0.5) : theme.spacing(1),
        width: '100%',
      }}
    >
      <PhoneHoursForm {...formProps} routingActionsEnabled={true} isSmallMode={isSmallMode} />
      {/* Cancel and Done buttons below the form */}
      <div
        css={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingTop: compact ? theme.spacing(1.5) : theme.spacing(5),
          borderTop: compact ? `1px solid ${theme.colors.neutral20}` : 'none',
          flexShrink: 0,
        }}
      >
        <div css={{ display: 'flex', gap: theme.spacing(2) }}>
          {onCancel && (
            <Button variant='secondary' size='large' onClick={onCancel}>
              {t('Cancel')}
            </Button>
          )}
          <Button size='large' onClick={handleSaveClick} disabled={!canSave}>
            {t('Done')}
          </Button>
        </div>
      </div>
    </div>
  );
};
